// Vendor Imports
import d3 from 'd3';
import $ from 'jquery';
import _ from 'lodash';

// Project Imports
import {
  formatValueHTML,
  formatValuePlainText
} from '../helpers/ColumnFormattingHelpers';
import { getMeasures } from '../helpers/measure';
import BaseVisualization from './BaseVisualization';
import { I18n } from 'common/i18n';
import { assert, assertHasProperties, assertInstanceOf, assertIsOneOfTypes } from 'common/assertions';
import formatString from 'common/js_utils/formatString';
import { renderLegend } from './BaseVisualization/Legend';

// Constants
import {
  AXIS_DEFAULT_COLOR,
  AXIS_GRID_COLOR,
  AXIS_LABEL_MARGIN,
  AXIS_TICK_COLOR,
  DEFAULT_LINE_HIGHLIGHT_FILL,
  DIMENSION_LABELS_FONT_COLOR,
  DIMENSION_LABELS_FONT_SIZE,
  FONT_STACK,
  GLYPH_SPACE_HEIGHT,
  MEASURE_LABELS_FONT_COLOR,
  MEASURE_LABELS_FONT_SIZE,
  MINIMUM_X_AXIS_TICK_DISTANCE,
  REFERENCE_LINES_STROKE_DASHARRAY,
  REFERENCE_LINES_STROKE_WIDTH,
  REFERENCE_LINES_UNDERLAY_THICKNESS
} from './SvgConstants';

import {
  getAxisLabels,
  getMeasureAxisMaxValue,
  getMeasureAxisMinValue,
  getReferenceLinesWithValues,
  getYAxisScalingMode
} from '../helpers/VifSelectors';

// These values have been eyeballed to provide enough space for axis labels
// that have been observed 'in the wild'. They may need to be adjusted slightly
// in the future, but the adjustments will likely be small in scale.
const MARGINS = {
  TOP: 32,
  RIGHT: 50,
  BOTTOM: 32,
  LEFT: 50
};
const COLUMN_MARGIN = 1;
const DIMENSION_LABELS_HEIGHT = 30;

// Just a safeguard; the data fetching code should never make a huge # of buckets.
const MAX_BUCKET_COUNT = 500;

// Ideally the chart is pure SVG to enable image exports, but D3 won't make
// good decisions on label placement unless we pre-load the styling we intend
// to use. We're doing this in JS as opposed to CSS files because:
// * No other chart styling is in CSS files.
// * Every value must be shared with the JS anyway.
// On completion of chart render, equivalent inline attributes will be applied
// to the nodes in question, so the chart should still render as pure SVG.
function bindStyles() {
  const styleElementId = 'socrata-histogram-styles';
  if ($(`#${styleElementId}`).length === 0) {
    $('head').append(`<style type="text/css" id="${styleElementId}">
      .histogram .axis text {
        font-family: ${FONT_STACK};
      }
      .histogram .y.axis text {
        font-size: ${MEASURE_LABELS_FONT_SIZE};
      }
      .histogram .x.axis text {
        font-size: ${DIMENSION_LABELS_FONT_SIZE};
      }
    </style>`);
  }
}

function SvgHistogram($element, vif, options) {
  var self = this;
  var $chartElement;
  var dataToRender;
  var d3DimensionXScale;
  var d3YScale;
  let referenceLines;
  let measures;

  _.extend(this, new BaseVisualization($element, vif, options));

  renderTemplate();

  /**
   * Public methods
   */

  this.render = function({
    newColumns,
    newComputedColumns,
    newData,
    newVif,
    newTableVif
  } = {}) {
    if (!newData && !dataToRender) {
      return;
    }

    this.clearError();

    if (newVif) {
      this.updateVif(newVif);
    }

    if (newData) {
      validateData(newData);
      dataToRender = newData;
    }

    if (newColumns || newComputedColumns) {
      this.updateColumns(newColumns, newComputedColumns);
    }

    if (newTableVif) {
      this.updateSummaryTableVif(newTableVif);
    }

    const allSeriesMeasureValues = dataToRender.map((dataResponse) => {
      const measureIndex = dataResponse.columns.indexOf('measure');
      return dataResponse.rows.map((row) => row[measureIndex]);
    });

    const onlyNullOrZeroValues = _(allSeriesMeasureValues).
      flatten().
      compact().
      isEmpty();

    if (onlyNullOrZeroValues) {
      self.renderNoDataError();
      return;
    }

    renderData();
  };

  this.invalidateSize = function() {

    if ($chartElement && dataToRender) {
      renderData();
    }
  };

  this.destroy = function() {

    d3.select(self.$element[0]).
      select('svg').
      remove();

    self.
      $element.
      find('.socrata-visualization-container').
      remove();
  };

  /**
   * Private methods
   */

  function renderTemplate() {

    bindStyles();

    $chartElement = $(
      '<div>',
      {
        'class': 'histogram'
      }
    );

    self.
      $element.
      find('.socrata-visualization-chart-container').
      append($chartElement);
  }

  function validateData(newData) {
    assertInstanceOf(newData, Array);
    const validateHasColumn = (series, column) =>
      assert(
        series.columns.indexOf(column) >= 0,
        `columns array must specify a ${column} column`
      );

    newData.forEach((series) => {
      assertHasProperties(series, 'rows', 'columns');
      validateHasColumn(series, 'bucket_start');
      validateHasColumn(series, 'bucket_end');
      validateHasColumn(series, 'measure');
      series.rows.forEach((row) => {
        assert(row.length === 3, `row has ${row.length} columns, expected 3`);
        row.forEach((item) => assertIsOneOfTypes(item, 'number'));
      });
    });

  }

  /**
   * Returns scaling to use ('linear' or 'logarithmic').
   *
   * Scale is determined by the method actually used to bucket the data. This
   * is a concern of the code passing us data to render, and is available per-
   * series at dataToRender[...].bucketType. If series specifiy different
   * bucketing methods, the first series scaling is used (TODO - revisit once
   * we support multiple series).
   *
   * Failing this, the default scale is linear.
   */
  function determineXScaleType(data) {
    return _.get(data, '[0].bucketType', 'linear');
  }

  function validateVif() {
    let currentVif = self.getVif();
    assert(
      currentVif.series.length <= 1,
      'Histogram does not currently support more than one series'
    );
  }

  function renderData() {
    validateVif();
    const firstSeries = dataToRender[0]; // TODO support multiple series.

    // Figure out which physical columns measure, bucket_start, and bucket_end live in.
    const measureIndex = firstSeries ? firstSeries.columns.indexOf('measure') : undefined;
    const bucketStartIndex = firstSeries ? firstSeries.columns.indexOf('bucket_start') : undefined;
    const bucketEndIndex = firstSeries ? firstSeries.columns.indexOf('bucket_end') : undefined;
    const measureFromRow = (row) => row[measureIndex];
    const bucketStartFromRow = (row) => row[bucketStartIndex];
    const bucketEndFromRow = (row) => row[bucketEndIndex];

    const xScaleType = determineXScaleType(dataToRender);

    const axisLabels = getAxisLabels(self.getVif());
    const firstNonFlyoutSeries = self.getFirstSeriesOfType(['histogram']);
    const rightMargin = MARGINS.RIGHT + (axisLabels.right ? AXIS_LABEL_MARGIN : 0);
    const topMargin = MARGINS.TOP + (axisLabels.top ? AXIS_LABEL_MARGIN : 0);
    const bottomMargin = MARGINS.BOTTOM + (axisLabels.bottom ? AXIS_LABEL_MARGIN : 0);

    let viewportHeight = Math.max(0, $chartElement.height() - topMargin - bottomMargin);

    const leftMargin = self.calculateLeftOrRightMargin({
      dataToRender: dataToRender[0],
      height: viewportHeight,
      isSecondaryAxis: false,
      series: firstNonFlyoutSeries
    }) + (axisLabels.left ? AXIS_LABEL_MARGIN : 0);

    let viewportWidth = Math.max(0, $chartElement.width() - leftMargin - rightMargin);

    // There may be instances where the chart is rendering in a hidden element. If this
    // happens, we shouldn't need to continue through this function. Allowing processing
    // to continue and calling `renderLegend()` would put the chart in an state where
    // parts of the chart don't render properly once they come into view (see EN-40617).
    if (viewportHeight <= 0 || viewportWidth <= 0) {
      return;
    }

    var chartWidth;
    var chartHeight;
    // TODO: If we end up supporting multiple series,
    // we'll have to figure out how to merge bucket ranges.
    // For now, this does a naive implementation.
    var groupedDataToRender = _.flatMap(dataToRender, (series) =>
      series.rows.map((row) => (
        {
          x0: bucketStartFromRow(row),
          x1: bucketEndFromRow(row),
          values: [measureFromRow(row)] // Note provision for multiple values.
        }
      ))
    );

    measures = getMeasures(self, dataToRender[0]);
    referenceLines = getReferenceLinesWithValues(self.getVif());

    var chartSvg;
    var viewportSvg;
    var xAxisAndSeriesSvg;
    var seriesSvg;
    var bucketGroupSvgs;
    var columnSvgs;
    var columnUnderlaySvgs;
    let referenceLineSvgs;
    let referenceLineUnderlaySvgs;
    let minYValue;
    let maxYValue;

    /**
     * Functions defined inside the scope of renderData() are stateful enough
     * to benefit from sharing variables within a single render cycle.
     */

    // See comment in renderXAxis() for an explanation as to why this is
    // separate.
    const column = _.get(self.getVif(), 'series[0].dataSource.dimension.columnName');
    const bindXAxisOnce = _.once(() => {
      const d3XAxis = d3.svg.axis().
        scale(d3DimensionXScale).
        orient('bottom').
        // X-axis labels display the column formatted value as is (no force humane)
        tickFormat((d) => formatValuePlainText(d, column, dataToRender[0])).
        outerTickSize(0);

      d3XAxis.ticks(Math.ceil(viewportWidth / MINIMUM_X_AXIS_TICK_DISTANCE));

      // d3's log scale potentially generates a large number of ticks.
      // Couldn't figure a way of limiting them sanely, so we'll just
      // generate 5 evenly-spaced ticks to use.
      if (xScaleType !== 'linear') {
        let range = d3DimensionXScale.range();
        let rangeMagnitude = range[1] - range[0];
        d3XAxis.tickValues(
          _.range(range[0], range[1], rangeMagnitude / 5).map(d3DimensionXScale.invert)
        );
      }

      xAxisAndSeriesSvg.
        append('g').
        attr('class', 'x axis').
        call(d3XAxis);

      // We don't need these for the baseline.
      d3XAxis.tickFormat('').tickSize(0);

      xAxisAndSeriesSvg.
        append('g').
        attr('class', 'x axis baseline').
        call(d3XAxis);
    });

    function renderXAxis() {
      var xAxisSvg;
      var xBaselineSvg;
      var baselineValue;

      // Binding the axis to the svg elements is something that only needs to
      // happen once even if we want to update the rendered properties more
      // than once; separating the bind from the layout in this way allows us
      // to treat renderXAxis() as idempotent.
      bindXAxisOnce();

      xAxisSvg = viewportSvg.select('.x.axis');
      xBaselineSvg = viewportSvg.select('.x.axis.baseline');

      xAxisSvg.
        attr(
          'transform',
          `translate(0,${chartHeight})`
        );

      xAxisSvg.selectAll('path').
        attr('fill', 'none').
        attr('stroke', AXIS_DEFAULT_COLOR).
        attr('shape-rendering', 'crispEdges');

      xAxisSvg.selectAll('line').
        attr('fill', 'none').
        attr('stroke', AXIS_TICK_COLOR).
        attr('shape-rendering', 'crispEdges');

      xAxisSvg.selectAll('text').
        attr('font-family', FONT_STACK).
        attr('font-size', `${DIMENSION_LABELS_FONT_SIZE}px`).
        attr('fill', DIMENSION_LABELS_FONT_COLOR).
        attr('stroke', 'none').
        attr(
          'data-row-index',
          (label, rowIndex) => rowIndex
        );

      if (minYValue > 0) {
        baselineValue = minYValue;
      } else if (maxYValue < 0) {
        baselineValue = maxYValue;
      } else {
        baselineValue = 0;
      }

      xBaselineSvg.
        attr(
          'transform',
          `translate(0,${d3YScale(baselineValue)})`
        ).
        selectAll('path').
          attr('fill', 'none').
          attr('stroke', AXIS_DEFAULT_COLOR).
          attr('shape-rendering', 'crispEdges');
    }

    // See comment in renderYAxis() for an explanation as to why this is
    // separate.
    const bindYAxisOnce = _.once(() => {
      const firstNonFlyoutSeries = self.getFirstSeriesOfType(['histogram']);
      const yAxis = self.generateYAxis({
        dataToRender,
        height: viewportHeight,
        isSecondaryAxis: false,
        scale: d3YScale,
        series: firstNonFlyoutSeries
      });

      chartSvg.select('.y.axis').
        call(yAxis);

      yAxis.
        tickSize(viewportWidth).
        tickFormat('');

      chartSvg.select('.y.grid').
        call(yAxis);
    });

    function renderYAxis() {
      var yAxisSvg = chartSvg.
        select('.y.axis');
      var yGridSvg = chartSvg.
        select('.y.grid');

      // Binding the axis to the svg elements is something that only needs to
      // happen once even if we want to update the rendered properties more
      // than once; separating the bind from the layout in this way allows us
      // to treat renderYAxis() as idempotent.
      bindYAxisOnce();

      yAxisSvg.selectAll('path').
        attr('fill', 'none').
        attr('stroke', AXIS_DEFAULT_COLOR).
        attr('shape-rendering', 'crispEdges');

      yAxisSvg.selectAll('line').
        attr('fill', 'none').
        attr('stroke', AXIS_TICK_COLOR).
        attr('shape-rendering', 'crispEdges');

      yAxisSvg.selectAll('text').
        attr('font-family', FONT_STACK).
        attr('font-size', `${MEASURE_LABELS_FONT_SIZE}px`).
        attr('fill', MEASURE_LABELS_FONT_COLOR).
        attr('stroke', 'none');

      yGridSvg.
        attr(
          'transform',
          'translate(' + (viewportWidth) + ',0)'
        );

      yGridSvg.selectAll('path').
        attr('fill', 'none').
        attr('stroke', 'none');

      yGridSvg.selectAll('line').
        attr('fill', 'none').
        attr('stroke', AXIS_GRID_COLOR).
        attr('shape-rendering', 'crispEdges');
    }

    function renderReferenceLines() {
      const getYPosition = (referenceLine) => d3YScale(referenceLine.value);
      const getLineThickness = (referenceLine) => {
        return self.isInRange(referenceLine.value, minYValue, maxYValue) ? REFERENCE_LINES_STROKE_WIDTH : 0;
      };

      const getUnderlayThickness = (referenceLine) => {
        return self.isInRange(referenceLine.value, minYValue, maxYValue) ? REFERENCE_LINES_UNDERLAY_THICKNESS : 0;
      };

      // This places the underlay half above the line and half below the line.
      const underlayUpwardShift = (REFERENCE_LINES_UNDERLAY_THICKNESS) / 2;

      referenceLineUnderlaySvgs.
        attr('data-reference-line-index', (referenceLine, index) => index).
        attr('fill', DEFAULT_LINE_HIGHLIGHT_FILL).
        attr('fill-opacity', 0).
        attr('x', 0).
        attr('y', (referenceLine) => getYPosition(referenceLine) - underlayUpwardShift).
        attr('width', chartWidth).
        attr('height', getUnderlayThickness);

      referenceLineSvgs.
        attr('shape-rendering', 'crispEdges').
        attr('stroke', (referenceLine) => referenceLine.color).
        attr('stroke-dasharray', REFERENCE_LINES_STROKE_DASHARRAY).
        attr('stroke-width', getLineThickness).
        attr('x1', 0).
        attr('y1', getYPosition).
        attr('x2', chartWidth).
        attr('y2', getYPosition);
    }

    // Note that renderXAxis(), renderYAxis() and renderSeries() all update the
    // elements that have been created by binding the data (which is done
    // inline in this function below).
    function renderSeries() {
      // Since we annotate each column-underlay and column element with its
      // series index using the 'data-series-index' attribute) on enter() we
      // can rely on that data attribute instead of having to derive the series
      // index from the individual datum bound to the element.
      function getPrimaryColorOrNone() {
        var seriesIndex = this.getAttribute('data-series-index');
        // To implement this, we simply need a way of looking up measures by seriesIndex.
        // This is easily done naively via _.find(measures, { seriesIndex });
        // For more speed, consider caching _.indexBy(measures, 'seriesIndex');
        // Anyway, not implemented for now because we only support one series.
        assert(seriesIndex === '0', 'Multiple series not supported in this chart yet');
        return measures[0].getColor();
      }

      bucketGroupSvgs.selectAll('.column-underlay').
        attr('y', 0).
        attr(
          'width',
          (d) => d3DimensionXScale(d.x1) - d3DimensionXScale(d.x0)
        ).
        attr('height', chartHeight).
        attr('stroke', 'none').
        attr('fill', 'transparent').
        attr('data-default-fill', getPrimaryColorOrNone);

      bucketGroupSvgs.selectAll('.column').
        attr(
          'y',
          function(d) {
            var yAttr;
            const value = maxYValue ? _.min([maxYValue, d.value]) : d.value;

            // If the value is zero or null we want it to be present at the
            // baseline for the rest of the bars (at the bottom of the chart
            // if the minimum value is 0 or more, at the top of the chart if
            // the maximum value is less than zero.
            if (value === 0 || !_.isFinite(value)) {

              if (minYValue > 0) {
                yAttr = d3YScale(minYValue) - 0.0001;
              } else if (maxYValue < 0) {
                yAttr = d3YScale(maxYValue) + 0.0001;
              } else {
                yAttr = d3YScale(0) - 0.0001;
              }

            } else if (value > 0) {

              if (minYValue > 0) {

                yAttr = Math.min(
                  d3YScale(value),
                  d3YScale(minYValue) - 1
                );
              } else {

                yAttr = Math.min(
                  d3YScale(value),
                  d3YScale(0) - 1
                );
              }

            } else if (value < 0) {

              if (value < minYValue) {
                yAttr = d3YScale(minYValue) + 1;
              } else if (maxYValue <= 0) {
                yAttr = _.max([d3YScale(maxYValue), 1]);
              } else {
                yAttr = d3YScale(0);
              }

            }

            return yAttr;
          }
        ).
        attr('x', COLUMN_MARGIN).
        attr(
          'width',
          (d) => Math.max(1, d3DimensionXScale(d.x1) - d3DimensionXScale(d.x0) - COLUMN_MARGIN)
        ).
        attr('height', (d) => {
          if (d.value === 0 || !_.isFinite(d.value)) {
            // We want the flyout for null or zero values to appear along
            // the x axis, rather than at the top of the chart.
            //
            // This means that we need to push the container element for
            // null values down to the x axis, rather than the default
            // behavior which places it at the top of the visualization
            // container. This is accomplished by the 'y' attribute, but
            // that does not have the expected behavior if the element is
            // not visible (or in this case, has a height of zero).
            //
            // Ultimately the way we force the column's container to
            // actually do the intended layout is to give the element a very
            // small height which should be more or less indiscernible,
            // which causes the layout to do the right thing.
            return 0.0001;
          } else {
            // Value of column clamped between min and max possible values.
            const value = _.clamp(d.value, minYValue, maxYValue);

            // Calculating baseline depending on column value
            // Value;
            //   > 0 : Baseline should be 0 or minYValue depending on which is lower.
            //   < 0 : Baseline should be 0 or maxYValue depending on which is higher.
            const baselineValue = value > 0 ? _.max([minYValue, 0]) : _.min([maxYValue, 0]);

            // Height / width calculated based on the difference between value and baseline
            const length = Math.abs(d3YScale(value) - d3YScale(baselineValue));

            // See comment about setting the y attribute above for the
            // rationale behind ensuring a minimum height of one pixel for
            // non-null and non-zero values.
            return _.max([1, length]);
          }
        }).
        attr('stroke', 'none').
        attr('fill', getPrimaryColorOrNone).
        attr('data-default-fill', getPrimaryColorOrNone);

      xAxisAndSeriesSvg.attr(
        'transform',
        'translate(0,0)'
      );
    }

    const adjustedViewportSize = renderLegend(self, {
      measures,
      referenceLines,
      viewportSize: {
        height: viewportHeight,
        width: viewportWidth
      }
    });

    viewportHeight = adjustedViewportSize.height;
    viewportWidth = adjustedViewportSize.width;

    chartWidth = viewportWidth;
    chartHeight = viewportHeight - DIMENSION_LABELS_HEIGHT;

    if (_(dataToRender).map('rows.length').max() >= MAX_BUCKET_COUNT) {
      self.renderError(
        formatString(
          I18n.t('shared.visualizations.charts.histogram.error_exceeded_max_bucket_count'),
          MAX_BUCKET_COUNT
        )
      );
      return;
    }

    /**
     * 2. Set up the x-scale
     */

    if (xScaleType === 'linear') {
      d3DimensionXScale = d3.scale.linear().domain(
        d3.extent(_.flatMap(
          dataToRender,
          (series) =>
            [
              d3.min(series.rows, bucketStartFromRow),
              d3.max(series.rows, bucketEndFromRow)
            ]
      )));
    } else {
      d3DimensionXScale = d3.scale.log().domain(
        d3.extent(_.flatMap(
          dataToRender,
          (series) =>
            [
              d3.min(series.rows, bucketStartFromRow),
              d3.max(series.rows, bucketEndFromRow)
            ]
      )));

      // Check if the domain includes or crosses zero.
      // If it does, it is impossible to display the data
      // on a log scale (as the scale would be infinite in
      // size). Another way to think about it is that the
      // range [0, 1] and [-1, 0] include an infinite number
      // of orders of magnitude.
      let domain = d3DimensionXScale.domain();
      let domainCrossesOrIncludesZero = (domain[0] * domain[1]) <= 0;
      if (domainCrossesOrIncludesZero) {
        self.renderError(
          I18n.t('shared.visualizations.charts.histogram.error_domain_includes_zero')
        );
        return;
      }
    }

    d3DimensionXScale.
      nice().
      rangeRound([0, chartWidth]);

    /**
     * 3. Set up the y-scale
     */

    let measureRowExtents = _.flatMap(
      dataToRender,
      (series) => d3.extent(series.rows, measureFromRow)
    );

    if (getYAxisScalingMode(self.getVif()) === 'showZero') {
      measureRowExtents.push(0);
    }

    const measureExtent = d3.extent(measureRowExtents);
    const dataMinYValue = getMinYValue(measureExtent[0], referenceLines);
    const dataMaxYValue = getMaxYValue(measureExtent[1], referenceLines);

    try {
      const limitMin = getMeasureAxisMinValue(self.getVif());
      const limitMax = getMeasureAxisMaxValue(self.getVif());

      if (limitMin && limitMax && limitMin >= limitMax) {
        self.renderError(
          I18n.t(
            'shared.visualizations.charts.common.validation.errors.' +
            'measure_axis_min_should_be_lesser_then_max'
          )
        );
        return;
      }

      minYValue = limitMin || _.min([dataMinYValue, 0]);
      maxYValue = limitMax || _.max([dataMaxYValue, 0]);

      if (limitMin || limitMax) {
        d3YScale = d3.scale.linear().
          domain([minYValue, maxYValue]).
          range([chartHeight, GLYPH_SPACE_HEIGHT]);
      } else {
        d3YScale = d3.scale.linear().
          domain([minYValue, maxYValue]).
          nice().
          range([chartHeight, GLYPH_SPACE_HEIGHT]);
      }

    } catch (error) {
      self.renderError(error.message);
      return;
    }

    /**
     * 4. Clear out any existing chart.
     */

    d3.select($chartElement[0]).
      select('svg').
      remove();

    /**
     * 5. Render the chart.
     */

     // Create the top-level <svg> element first.
    chartSvg = d3.select($chartElement[0]).append('svg').
      attr('width', viewportWidth + leftMargin + rightMargin).
      attr('height', viewportHeight + topMargin + bottomMargin);

    // The viewport represents the area within the chart's container that can
    // be used to draw the x-axis, y-axis and chart marks.
    viewportSvg = chartSvg.append('g').
      attr('class', 'viewport').
      attr('transform', `translate(${leftMargin}, ${topMargin})`);

    viewportSvg.append('g').
      attr('class', 'y axis');

    viewportSvg.append('g').
      attr('class', 'y grid');

    xAxisAndSeriesSvg = viewportSvg.append('g').
      attr('class', 'x-axis-and-series');

    xAxisAndSeriesSvg.append('g').
      attr('class', 'series');

    seriesSvg = xAxisAndSeriesSvg.select('.series');

    bucketGroupSvgs = seriesSvg.selectAll('.bucket-group').
      data(groupedDataToRender);

    bucketGroupSvgs.enter().append('g').
      attr('class', 'bucket-group').
      attr(
        'transform',
        function(d) {
          return 'translate(' + d3DimensionXScale(d.x0) + ',0)';
        }
      );

    /*
     * From: [
     *   { x0: A, x1: B, values: [X, Y] }
     *   { x0: C, x1: D, values: [W, Z] }
     * ]
     *
     * To:[
     *   { x0: A, x1: B, value: X },
     *   { x0: A, x1: B, value: Y },
     *
     *   { x0: C, x1: D, value: W },
     *   { x0: C, x1: D, value: Z }
     * ]
     */
    const toIndividualColumns = (d) =>
      d.values.map((value) => ({ x0: d.x0, x1: d.x1, value: value }));

    columnUnderlaySvgs = bucketGroupSvgs.selectAll('rect.column-underlay').data(toIndividualColumns);

    columnUnderlaySvgs.enter().append('rect').
      attr('class', 'column-underlay').
      attr(
        'data-series-index',
        /* eslint-disable no-unused-vars */
        function(datum, seriesIndex, rowIndex) {
        /* eslint-enable no-unused-vars */
          return seriesIndex;
        }
      ).
      attr(
        'data-row-index',
        // Not sure what the second argument actually is, but it is
        // the third argument that seems to track the row index.
        function(datum, seriesIndex, rowIndex) {
          return rowIndex;
        }
      );

    columnSvgs = bucketGroupSvgs.selectAll('rect.column').data(toIndividualColumns);
    columnSvgs.enter().append('rect').
      attr('class', 'column').
      attr(
        'data-series-index',
        /* eslint-disable no-unused-vars */
        function(datum, seriesIndex, rowIndex) {
        /* eslint-enable no-unused-vars */
          return seriesIndex;
        }
      ).
      attr(
        'data-row-index',
        // Not sure what the second argument actually is, but it is
        // the third argument that seems to track the row index.
        function(datum, seriesIndex, rowIndex) {
          return rowIndex;
        }
      );

    referenceLineSvgs = seriesSvg.selectAll('line.reference-line').
      data(referenceLines).
      enter().
      append('line').
      attr('class', 'reference-line');

    referenceLineUnderlaySvgs = seriesSvg.selectAll('rect.reference-line-underlay').
      data(referenceLines).
      enter().
      append('rect').
      attr('class', 'reference-line-underlay');

    renderXAxis();
    renderSeries();
    renderYAxis();
    renderReferenceLines();

    /**
     * 6. Set up event handlers for mouse interactions.
     */

    function hideHighlight() {
      chartSvg.selectAll('.column').each(function() {
        this.setAttribute('fill', this.getAttribute('data-default-fill'));
      });
    }

    bucketGroupSvgs.selectAll('.column-underlay, .column').
      on(
        'mousemove',
        function(datum) {
          const columnElement = this.parentNode.querySelector('.column');

          showColumnHighlight(columnElement);
          showColumnFlyout(
            columnElement,
            datum,
            parseInt(this.getAttribute('data-series-index'), 10)
          );
        }
      ).
      on(
        'mouseleave',
        function() {
          hideHighlight();
          hideFlyout();
        }
      );

    seriesSvg.selectAll('.reference-line-underlay').
      // NOTE: The below function depends on this being set by d3, so it is
      // not possible to use the () => {} syntax here.
      on('mousemove', function() {
        if (!isCurrentlyPanning()) {
          const underlayHeight = parseInt($(this).attr('height'), 10);
          const flyoutOffset = {
            left: d3.event.clientX,
            top: $(this).offset().top + (underlayHeight / 2) - window.pageYOffset
          };

          self.showReferenceLineFlyout({
            dataToRender,
            element: this,
            flyoutOffset,
            referenceLines
          });

          $(this).attr('fill-opacity', 1);
        }
      }).
      // NOTE: The below function depends on this being set by d3, so it is
      // not possible to use the () => {} syntax here.
      on('mouseleave', function() {
        if (!isCurrentlyPanning()) {
          hideFlyout();
          $(this).attr('fill-opacity', 0);
        }
      });

    chartSvg.selectAll('text').
      attr('cursor', 'default');

    self.renderAxisLabels(chartSvg, {
      x: leftMargin,
      y: topMargin,
      width: viewportWidth,
      height: viewportHeight - DIMENSION_LABELS_HEIGHT
    });
  }

  function isCurrentlyPanning() {
    // EN-10810 - Bar Chart flyouts do not appear in Safari
    //
    // Internet Explorer will apparently always return a non-zero value for
    // d3.event.which and even d3.event.button, so we need to check
    // d3.event.buttons for a non-zero value (which indicates that a button is
    // being pressed).
    //
    // Safari apparently does not support d3.event.buttons, however, so if it
    // is not a number then we will fall back to d3.event.which to check for a
    // non-zero value there instead.
    //
    // Chrome appears to support both cases, and in the conditional below
    // Chrome will check d3.event.buttons for a non-zero value.
    return (_.isNumber(d3.event.buttons)) ?
      d3.event.buttons !== 0 :
      d3.event.which !== 0;
  }

  function showColumnHighlight(columnElement) {

    var selection = d3.select(columnElement);

    selection.attr(
      'fill',
      function() {
        var seriesIndex = this.getAttribute('data-series-index');
        var highlightColor = self.getHighlightColorBySeriesIndex(
          seriesIndex
        );

        return (highlightColor !== null) ?
          highlightColor :
          selection.attr('fill');
      }
    );
  }

  function bucketTitleHTML(bucketData) {
    // TODO units
    const column = _.get(self.getVif(), 'series[0].dataSource.dimension.columnName');
    // Flyout labels display the column formatted value as is (no force humane)
    return `${formatValueHTML(bucketData.x0, column, dataToRender[0])} to ${formatValueHTML(bucketData.x1, column, dataToRender[0])}`;
  }

  function seriesLabel(seriesIndex) {
    return _.get(
      self.getVif(), ['series', seriesIndex, 'label'],
      I18n.t('shared.visualizations.charts.common.flyout_value_label')
    );
  }

  function showColumnFlyout(columnElement, datum, seriesIndex) {

    assert(seriesIndex === 0, 'Multiple series not supported in this chart yet');

    const titleHTML = bucketTitleHTML(datum);
    const label = seriesLabel(seriesIndex);
    const value = datum.value;
    let valueHTML;

    const $titleRow = $('<tr>', { 'class': 'socrata-flyout-title' }).
      append(
        $('<td>', { 'colspan': 3 }).
          html(
            (titleHTML) ? titleHTML : ''
          )
        );

    const $colorCell = $('<td>', { 'class': 'socrata-flyout-cell' });
    const $labelCell = $('<td>', { 'class': 'socrata-flyout-cell' }).
      text(label).
      css('color', measures[0].getColor());
    const $valueCell = $('<td>', { 'class': 'socrata-flyout-cell' });
    const $valueRow = $('<tr>', { 'class': 'socrata-flyout-row' });
    const $table = $('<table>', { 'class': 'socrata-flyout-table' });

    if (value === null) {
      valueHTML = I18n.t('shared.visualizations.charts.common.no_value');
    } else {
      const unitOther = self.getUnitOtherBySeriesIndex(seriesIndex);
      const unitOne = self.getUnitOneBySeriesIndex(seriesIndex);

      const column = _.get(self.getVif(), `series[${seriesIndex}].dataSource.measure.columnName`);
      // Flyout labels display the column formatted value as is (no force humane)
      valueHTML = formatValueHTML(value, column, dataToRender[0]);

      if (value == 1) {
        valueHTML += ` ${_.escape(unitOne)}`;
      } else {
        valueHTML += ` ${_.escape(unitOther)}`;
      }
    }

    $valueCell.
      html(valueHTML);

    $valueRow.append([
      $colorCell,
      $labelCell,
      $valueCell
    ]);

    $table.append([
      $titleRow,
      $valueRow
    ]);

    const payload = {
      element: columnElement,
      content: $table,
      rightSideHint: false,
      belowTarget: false,
      dark: true
    };

    self.emitEvent(
      'SOCRATA_VISUALIZATION_FLYOUT',
      payload
    );
  }

  function hideFlyout() {
    self.emitEvent(
      'SOCRATA_VISUALIZATION_FLYOUT',
      null
    );
  }

  function getMinYValue(measureExtent, referenceLines) {
    const minReferenceLinesValue = d3.min(
      referenceLines.map(
        (referenceLine) => referenceLine.value
      )
    );

    return d3.min([measureExtent, minReferenceLinesValue]);
  }

  function getMaxYValue(measureExtent, referenceLines) {
    const maxReferenceLinesValue = d3.max(
      referenceLines.map(
        (referenceLine) => referenceLine.value
      )
    );

    return d3.max([measureExtent, maxReferenceLinesValue]);
  }
}

export default SvgHistogram;
